import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { firstValueFrom, Observable } from 'rxjs';
import { IgxGridModule } from '@infragistics/igniteui-angular';

import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { InvoiceService } from 'src/app/core/services/data/invoice.service';
import { QuicksearchService } from 'src/app/core/services/data/quicksearch.service';
import { dbErrorEntry } from 'src/app/shared/entities/dbErrorEntry';
import { QuickSearchCriteria } from 'src/app/shared/entities/QuickSearchCriteria';
import { QuickSearchResults } from 'src/app/shared/entities/QuickSearchResults';
import { ApnUtilitiesService } from 'src/app/core/services/data/apn-utilities.service';
import { IEntityIdCustomerTypeRequest, IEntityIdEmailAdrressResponse } from 'src/app/shared/interfaces';
import { environment } from 'src/environments/environment';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ActivatedRoute, Router } from '@angular/router';

type SendInvoiceGridRow = QuickSearchResults & {
  selectedForSend: boolean;
  isEmailed: boolean;
  notificationStatus: 'success' | 'error' | null;
};

@Component({
  selector: 'app-send-invoices',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatSnackBarModule,
    IgxGridModule,
  ],
  templateUrl: './send-invoices.component.html',
  styleUrl: './send-invoices.component.scss',
})
export class SendInvoicesComponent implements OnInit, OnChanges, AfterViewInit {
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(DataService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly quicksearchService = inject(QuicksearchService);
  private readonly appGeneralState = inject(AppGeneralStateService);
  private readonly apnUtilitiesService = inject(ApnUtilitiesService);
  private readonly messageDialogService = inject(MessageDialogService);
  private readonly router = inject(Router);

  @Input() quickSearchCriteria!: QuickSearchCriteria;
  @Input('quickSearchResults') incomingQuickSearchResults: QuickSearchResults[] = [];
  @Input() set setIfSenderEmailAddress(emailAddress: string | null) {
    this.senderEmailAddress = emailAddress;
    this.noSenderEmailAddress = !emailAddress || emailAddress.trim().length === 0;
  }

  @Output() quickSearchResultsLoading = new EventEmitter<boolean>();
  @Output() formDisplayed = new EventEmitter<void>();

  quickSearchResults: SendInvoiceGridRow[] = [];
  rowsReturned: number = 0;
  hasCompletedSearch: boolean = false;

  invoiceUserComments: string = '';
  isProcessingNotifications: boolean = false;
  isSendingEmails: boolean = false;
  currentNotificationName: string = '';
  currentNotificationInvoiceNumber: string = '';
  totalRowsToProcess: number = 0;
  completedRows: number = 0;
  parkerIdEmailList: IEntityIdEmailAdrressResponse[] = [];
  readonly urlDomain: string = '';
  lotNumber: string;
  senderEmailAddress: string | null = null;
  noSenderEmailAddress: boolean = false;

  constructor() {
    this.urlDomain = environment.UrlDomain;
  }

  async ngOnInit(): Promise<void> {
    this.lotNumber = this.appGeneralState.currentLotNumber;

    // Always start this view with an empty grid on first load.
    this.quickSearchResults = [];
    this.rowsReturned = 0;
    this.hasCompletedSearch = false;

    this.appGeneralState.lotNumberSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.quickSearchResults = [];
      this.rowsReturned = 0;
      this.hasCompletedSearch = false;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['incomingQuickSearchResults']) {
      this.applyIncomingResults(this.incomingQuickSearchResults ?? []);
    }

    const criteriaChange = changes['quickSearchCriteria'];
    if (!criteriaChange || criteriaChange.firstChange || !this.quickSearchCriteria) {
      return;
    }

    this.runQuery(this.quickSearchCriteria);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.formDisplayed.emit());
  }

  onDisplayInvoice(invoiceNumber: string, lotNumber: string, entityId: string): void {
    this.messageDialogService.snackBarTopCenterTimed(`Generating Invoice for ${invoiceNumber}...`, 2000);

    if (invoiceNumber.substring(0, 2) == 'MI') {
      const invoiceViewUrl = this.router.serializeUrl(
        this.router.createUrlTree(['/viewinvoice', entityId, lotNumber, invoiceNumber]),
      );
      const w = window.open(invoiceViewUrl, '_blank');

      if (!w) {
        console.error('Failed to open new window. Popup blocker might be active.');
      }
    } else {
      const safeEntityId = entityId && entityId.trim().length > 0 ? entityId : 'na';
      const invoiceViewUrl = this.router.serializeUrl(
        this.router.createUrlTree(['/viewinvoice', safeEntityId, lotNumber, invoiceNumber]),
      );
      const w = window.open(invoiceViewUrl, '_blank');

      if (!w) {
        console.error('Failed to open new window. Popup blocker might be active.');
      }
    }
  }

  get selectedToEmail(): number {
    return this.quickSearchResults.filter((row) => row.selectedForSend && !row.isEmailed).length;
  }

  get selectedRows(): number {
    return this.selectedToEmail;
  }

  get emailedCount(): number {
    return this.quickSearchResults.filter((row) => row.isEmailed).length;
  }

  get sendProgressValue(): number {
    if (!this.totalRowsToProcess) {
      return 0;
    }

    return (this.completedRows / this.totalRowsToProcess) * 100;
  }

  get isLoadingData(): boolean {
    return this.rowsReturned === -1;
  }

  onSelectedForSendChange(row: SendInvoiceGridRow, checked: boolean): void {
    row.selectedForSend = checked;
  }

  selectAllToEmail(): void {
    this.quickSearchResults.forEach((row) => {
      if (!row.isEmailed) {
        row.selectedForSend = true;
      }
    });
  }

  clearAllToEmail(): void {
    this.quickSearchResults.forEach((row) => {
      if (!row.isEmailed) {
        row.selectedForSend = false;
      }
    });
  }

  async onSendInvoices(): Promise<void> {
    const selectedRows = this.quickSearchResults.filter((row) => row.selectedForSend && !row.isEmailed);

    if (!selectedRows.length) {
      return;
    }

    this.isProcessingNotifications = true;
    this.isSendingEmails = true;
    this.totalRowsToProcess = selectedRows.length;
    this.completedRows = 0;

    try {
      for (const row of selectedRows) {
        this.currentNotificationName = row.customerName ?? '';
        this.currentNotificationInvoiceNumber = row.invoiceNumber ?? '';

        const sendSucceeded = await this.onSendInvoice(row.invoiceNumber ?? '');
        row.notificationStatus = sendSucceeded ? 'success' : 'error';
        row.isEmailed = sendSucceeded;
        this.completedRows += 1;

        if (sendSucceeded) {
          row.selectedForSend = false;
          row.dateSent = new Date().toISOString();
          this.onUpdateInvoiceSendDate(row.invoiceNumber ?? '');
          this.quickSearchResults = [...this.quickSearchResults];
        }
      }

      this.snackBar.open('Finished sending invoice notification emails.', 'Close', {
        duration: 5000,
        panelClass: ['send-invoices-complete-snackbar'],
        verticalPosition: 'top',
        horizontalPosition: 'center',
      });
    } finally {
      this.isProcessingNotifications = false;
      this.isSendingEmails = false;
    }
  }

  private async onSendInvoice(invoiceNumber: string): Promise<boolean> {
    try {
      const status = await firstValueFrom(
        this.dataService
          .sendInvoiceNotificationEmailWithComments(invoiceNumber, this.invoiceUserComments)
          .pipe(takeUntilDestroyed(this.destroyRef)),
      );

      return status;
    } catch {
      return false;
    }
  }

  private runQuery(criteria: QuickSearchCriteria): void {
    this.rowsReturned = -1;
    this.hasCompletedSearch = false;
    this.quickSearchResultsLoading.emit(true);

    const criteriaToUse = { ...criteria };
    if ((criteriaToUse.invoiceNumber ?? '') !== '') {
      criteriaToUse.zeroBalance = 1;
    }

    this.getInvoiceQuery(criteriaToUse)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => {
          this.hasCompletedSearch = true;
          this.applyIncomingResults(results ?? []);
          this.quicksearchService.setQuickSearchResults(results ?? []);
        },
        error: (err: unknown) => {
          this.rowsReturned = 0;
          this.hasCompletedSearch = true;
          this.logError('Err: send-invoices.component.ts::runQuery().', err);
          this.snackBar.open(
            'An unexpected error occurred while searching for invoices. Please try again or contact APN support.',
            '',
            { duration: 7000 },
          );
          this.quickSearchResultsLoading.emit(false);
        },
      });
  }

  private getInvoiceQuery(criteria: QuickSearchCriteria): Observable<QuickSearchResults[]> {
    switch (criteria.zeroBalance) {
      case 1:
        return this.invoiceService.getQuickSearchZeroBalanceInvoiceList(criteria);
      case 2:
        return this.invoiceService.getQuickSearchOnlyZeroBalanceInvoiceList(criteria);
      case 3:
        return this.invoiceService.getQuickSearchOnlyCreditBalanceInvoiceList(criteria);
      case 4:
        return this.invoiceService.getQuickSearchPastDueBalanceInvoiceList(criteria);
      case 0:
      default:
        return this.invoiceService.getQuickSearchNonZeroBalanceInvoiceList(criteria);
    }
  }

  private applyIncomingResults(value: QuickSearchResults[]): void {
    const rows = (value ?? []).filter((row) => this.isEmailDelivery(row));

    this.quickSearchResults = rows.map((row) => ({
      ...row,
      originalBalance: row.originalBalance ?? row.currentBalance,
      selectedForSend: (row as SendInvoiceGridRow).selectedForSend ?? false,
      isEmailed: (row as SendInvoiceGridRow).isEmailed ?? false,
      notificationStatus: (row as SendInvoiceGridRow).notificationStatus ?? null,
    }));

    this.rowsReturned = this.quickSearchResults.length;
    void this.addEmailAddressesToInvoiceSearchResults(rows);
    this.quickSearchResultsLoading.emit(false);
  }

  private isEmailDelivery(row: QuickSearchResults): boolean {
    const rawDelivery = (row as any)?.delivery ?? (row as any)?.deliver ?? '';
    return String(rawDelivery).trim().toLowerCase() === 'email';
  }

  private logError(location: string, error: unknown): void {
    const entry = new dbErrorEntry();
    entry.Location = location;
    entry.errorMessage = JSON.stringify(error);
    this.apnUtilitiesService.logErrorToDB(entry);
  }

  async addEmailAddressesToInvoiceSearchResults(quickSearchResults: QuickSearchResults[]): Promise<void> {
    const entityIdList: IEntityIdCustomerTypeRequest[] = this.getParkeIdListFromInvoiceList(quickSearchResults);
    var parkerIdEmailList: IEntityIdEmailAdrressResponse[] = [];

    if (!entityIdList.length) {
      this.parkerIdEmailList = [];
      this.quickSearchResults = [];
      this.rowsReturned = 0;
      return;
    }

    parkerIdEmailList = await this.getHttpEmailAddressForParkers(entityIdList);

    this.quickSearchResults = this.quickSearchResults
      .map((row) => {
        const emailEntry = parkerIdEmailList.find((entry) => entry.entityId === row.entityId);
        const rawIsPortal =
          (emailEntry as any)?.isPortal ?? (emailEntry as any)?.IsPortal ?? (emailEntry as any)?.isAcePortal;
        return {
          ...row,
          emailAddress: emailEntry?.emailAddress ?? '',
          IsPortal:
            rawIsPortal === undefined
              ? (row as any).IsPortal ?? false
              : rawIsPortal === true || rawIsPortal === 1 || String(rawIsPortal).trim().toLowerCase() === 'true',
        };
      })
      .filter((row) => String((row as any).emailAddress ?? '').trim() !== '');

    this.rowsReturned = this.quickSearchResults.length;
  }

  getParkeIdListFromInvoiceList(quickSearchResults: QuickSearchResults[]): IEntityIdCustomerTypeRequest[] {
    const seen = new Set<string>();
    return quickSearchResults
      .filter((row) => typeof row.entityId === 'string' && row.entityId.trim() !== '')
      .filter((row) => !seen.has(row.entityId) && seen.add(row.entityId))
      .map((row) => ({ entityId: row.entityId, customerType: row.customerType ?? '' }));
  }

  async getHttpEmailAddressForParkers(
    entityIdList: IEntityIdCustomerTypeRequest[],
  ): Promise<IEntityIdEmailAdrressResponse[]> {
    try {
      this.parkerIdEmailList = await firstValueFrom(
        this.dataService.getMatchingEmailAddressesListForParker(entityIdList),
      );
    } catch (error: unknown) {}
    return this.parkerIdEmailList;
  }

  onUpdateInvoiceSendDate(invoiceNumber: string): void {
    const dateSent = new Date();
    this.dataService
      .updateInvoiceNotificationEmailDateSent(invoiceNumber, dateSent.toISOString().split('T')[0])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {},
        complete: () => {},
      });
  }
}
